import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogOut, FileText, AlertCircle, Loader, Home, Clock, CheckCircle, BarChart3, ChevronRight, Eye, ArrowLeft, ChevronDown, Check, Settings, Plus, Edit2, Trash2, Download, Upload, TrendingUp, Users, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ============ MAIN APP COMPONENT ============
export default function PolicyPage() {
  const { encryptedId } = useParams();
  const navigate = useNavigate();
  
  const [employee, setEmployee] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [policyStats, setPolicyStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [policyViews, setPolicyViews] = useState({});
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [stats, setStats] = useState(null);



const fetchData = useCallback(async () => {
  try {
    if (!encryptedId || encryptedId === 'invalid') {
      setError('Invalid access link. Please request a new one from HR.');
      setLoading(false);
      return;
    }

    const empResponse = await fetch(`${API_URL}/api/employee/${encryptedId}`);
    if (!empResponse.ok) {
      throw new Error('Failed to decrypt employee ID. Invalid link.');
    }

    const empData = await empResponse.json();
    
    if (!empData.employee) {
      throw new Error('Employee not found');
    }

    setEmployee(empData.employee);
    sessionStorage.setItem('employeeId', empData.employee.display_emp_id);
    sessionStorage.setItem('encryptedId', encryptedId);

    const adminIds = ['0000', '0010', '12345']; // Example admin IDs
    const isAdminUser = adminIds.includes(String(empData.employee.display_emp_id));
    setIsAdmin(isAdminUser);

    const polResponse = await fetch(`${API_URL}/api/policies`);
    if (!polResponse.ok) {
      throw new Error('Failed to fetch policies');
    }

    const polData = await polResponse.json();
    
    // Filter policies for non-admin users
    let filteredPolicies = polData.policies || [];
    if (!isAdminUser) {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Reset to midnight for date comparison
      
      filteredPolicies = filteredPolicies.filter(policy => {
        const releaseDate = new Date(policy.release_date);
        releaseDate.setHours(0, 0, 0, 0);
        
        // Only show published policies with release_date <= current date
        return policy.status === 'published' && releaseDate <= currentDate;
      });
    }
    
    setPolicies(filteredPolicies);

    // Get user's policy review status with acceptance
    const statusResponse = await fetch(
      `${API_URL}/api/policy/user-status/${empData.employee.display_emp_id}`
    );
    if (statusResponse.ok) {
      const statusData = await statusResponse.json();
      const viewsMap = {};
      statusData.statuses?.forEach(status => {
        viewsMap[status.document_id] = {
          viewed: true,
          accepted: status.user_read_accept === 'yes',
          view_count: status.view_count,
          last_viewed: status.last_viewed
        };
      });
      console.log(viewsMap)
      setPolicyViews(viewsMap);
    }

    if (filteredPolicies.length > 0) {
      const stats = {};
      for (const policy of filteredPolicies) {
        try {
          const statsRes = await fetch(`${API_URL}/api/policy/${policy.id}/stats`);
          if (statsRes.ok) {
            const statsData = await statsRes.json();
            // stats[policy.id] = statsData;
            stats[String(policy.id)] = statsData;
          }
        } catch (err) {
          console.log('Could not fetch stats for policy', policy.id);
        }
      }
      console.log('excel', stats);
      setPolicyStats(stats);
    }

    setLoading(false);
  } catch (err) {
    console.error('Error:', err);
    setError(err.message || 'Failed to load data');
    setLoading(false);
  }
}, [encryptedId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

 

  const handleLogout = () => {
    sessionStorage.clear();
    setIsLogoutModalOpen(true);
  };

  const handleStay = () => {
    setIsLogoutModalOpen(false);
  };

const handleCloseTab = () => {
  setIsLogoutModalOpen(false);
  sessionStorage.clear();
  
  // Set window.opener to allow close
  window.opener = window;
  
  // Close the window
  window.close();
  
  // If close failed (after 100ms), show close instruction with auto-close attempt
  setTimeout(() => {
    document.open();
    document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Logged Out</title>
        <style>
          body {
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100vh;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
          .container {
            background: white;
            padding: 50px;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            text-align: center;
            max-width: 500px;
          }
          h1 { color: #333; margin: 20px 0 10px; }
          p { color: #666; font-size: 18px; margin-bottom: 30px; }
          .emoji { font-size: 60px; }
          .hint { color: #999; font-size: 14px; margin: 20px 0; }
          button {
            padding: 15px 40px;
            background: #667eea;
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s;
          }
          button:hover { background: #5568d3; transform: translateY(-2px); }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="emoji">👋</div>
          <h1>Successfully Logged Out</h1>
          <p>You can safely close this tab now.</p>
          <button id="closeBtn">Close This Tab</button>
          <div class="hint">
            Or press <strong>Ctrl+W</strong> (Windows) / <strong>Cmd+W</strong> (Mac)
          </div>
        </div>
        <script>
          function attemptClose() {
            // Method 1: Try direct close
            window.close();
            
            // Method 2: Simulate Ctrl+W keyboard event
            const ctrlWEvent = new KeyboardEvent('keydown', {
              key: 'w',
              code: 'KeyW',
              keyCode: 87,
              which: 87,
              ctrlKey: true,
              metaKey: navigator.platform.includes('Mac'),
              bubbles: true,
              cancelable: true
            });
            document.dispatchEvent(ctrlWEvent);
            
            // Method 3: Open blank and close
            setTimeout(() => {
              window.opener = null;
              window.open('', '_self');
              window.close();
            }, 100);
          }
          
          // Attach to button
          document.getElementById('closeBtn').addEventListener('click', attemptClose);
          
          // Auto-attempt to close after 1 second
          setTimeout(attemptClose, 1000);
        </script>
      </body>
      </html>
    `);
    document.close();
  }, 100);
};

  const handleViewDetails = (policyId) => {
    if (isAdmin) {
      setSelectedPolicyId(policyId);
      setCurrentView('policy-details');
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!employee) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar 
        isOpen={isSidebarOpen}
        isAdmin={isAdmin}
        currentView={currentView}
        onViewChange={setCurrentView}
        employee={employee}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          employee={employee}
          isAdmin={isAdmin}
          onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          userMenuOpen={userMenuOpen}
          onUserMenuToggle={() => setUserMenuOpen(!userMenuOpen)}
          onLogout={handleLogout}
        />

        <main className="flex-1 overflow-auto">
          <AnimatePresence mode="wait">
            {currentView === 'dashboard' && (
              <Dashboard 
                key="dashboard"
                employee={employee} 
                isAdmin={isAdmin} 
                policies={policies} 
                policyViews={policyViews}
                policyStats={policyStats}
                encryptedId={encryptedId}
              />
            )}
            {currentView === 'policies' && (
              <PoliciesView 
                key="policies"
                employee={employee} 
                policies={policies} 
                encryptedId={encryptedId} 
                policyViews={policyViews}
                policyStats={policyStats}
                isAdmin={isAdmin}
                onViewDetails={handleViewDetails}
              />
            )}
            {currentView === 'policy-details' && isAdmin && (
              <PolicyDetailsPage
                key="policy-details"
                policyId={selectedPolicyId}
                policyName={policies.find(p => p.id === selectedPolicyId)?.title}
                onBack={() => setCurrentView('policies')}
              />
            )}
            {currentView === 'admin-policies' && isAdmin && (
            <AdminPolicyPanel 
              key="admin-policies" 
              isAdmin={isAdmin} 
              employee={employee} 
            />
          )}
          </AnimatePresence>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onStay={handleStay}
        onCloseTab={handleCloseTab}
        onCancel={() => setIsLogoutModalOpen(false)}
      />
    </div>
  );
}

// ============ LOGOUT CONFIRMATION MODAL ============
function LogoutConfirmationModal({ isOpen, onStay, onCloseTab, onCancel }) {
    if (!isOpen) return null;

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.7 },
        visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25 } },
        exit: { opacity: 0, scale: 0.7, transition: { duration: 0.15 } }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={onCancel}>
                    <motion.div
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={e => e.stopPropagation()}
                        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <AlertCircle className="w-6 h-6 text-red-500" />
                                Confirm Logout
                            </h3>
                            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <p className="text-gray-700 mb-6">
                            You have been logged out. Would you like to <strong>close this tab</strong> or <strong>stay on this page</strong>?
                        </p>

                        <div className="flex justify-end gap-3">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={onStay}
                                className="px-4 py-2 bg-indigo-500 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-600 transition"
                            >
                                Stay on this page
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={onCloseTab}
                                className="px-4 py-2 bg-red-500 text-white font-semibold rounded-lg shadow-md hover:bg-red-600 transition"
                            >
                                Logged Out
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

// ============ LOADING SCREEN ============
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity }}
        className="w-12 h-12 border-4 border-indigo-300 border-t-indigo-600 rounded-full"
      />
    </div>
  );
}

// ============ ERROR SCREEN ============
function ErrorScreen({ error }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center"
      >
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid Access Link</h1>
        <p className="text-gray-600">{error}</p>
      </motion.div>
    </div>
  );
}

// ============ SIDEBAR ============
function Sidebar({ isOpen, isAdmin, currentView, onViewChange, employee, onLogout }) {
  const userMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
  ];

  const adminMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'policies', label: 'View Policies', icon: FileText },
    { id: 'admin-policies', label: 'Manage Policies', icon: Settings },
  ];

  const menuItems = isAdmin ? adminMenuItems : userMenuItems;

  return (
    <motion.div
      animate={{ x: isOpen ? 0 : -300 }}
      className={`${isOpen ? 'w-64' : 'w-0'} bg-gray-900 text-white transition-all duration-300 overflow-hidden flex flex-col fixed h-screen z-40 lg:relative`}
    >
      <SidebarHeader isAdmin={isAdmin} />
      <SidebarNav menuItems={menuItems} currentView={currentView} onViewChange={onViewChange} />
      <SidebarFooter onLogout={onLogout} />
    </motion.div>
  );
}

// ============ SIDEBAR HEADER ============
function SidebarHeader({ isAdmin }) {
  return (
    <div className="p-6 border-b border-gray-800">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
          <FileText className="w-6 h-6" />
        </div>
        <div>
          <h2 className="font-bold text-lg">HR Policy</h2>
          <p className="text-xs text-gray-400">{isAdmin ? '👤 Admin' : 'Employee'}</p>
        </div>
      </div>
    </div>
  );
}

// ============ SIDEBAR NAV ============
function SidebarNav({ menuItems, currentView, onViewChange }) {
  return (
    <nav className="flex-1 p-4 space-y-2">
      {menuItems.map((item) => (
        <motion.button
          key={item.id}
          whileHover={{ x: 5 }}
          onClick={() => onViewChange(item.id)}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
            currentView === item.id
              ? 'bg-indigo-600 text-white'
              : 'text-gray-300 hover:bg-gray-800'
          }`}
        >
          <item.icon className="w-5 h-5" />
          <span className="font-medium">{item.label}</span>
          {currentView === item.id && <ChevronRight className="w-4 h-4 ml-auto" />}
        </motion.button>
      ))}
    </nav>
  );
}

// ============ SIDEBAR FOOTER ============
function SidebarFooter({ onLogout }) {
  return (
    <div className="p-4 border-t border-gray-800">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-red-600/20 hover:text-red-300 transition"
      >
        <LogOut className="w-5 h-5" />
        <span className="font-medium">Logout</span>
      </motion.button>
    </div>
  );
}

// ============ HEADER ============
function Header({ employee, isAdmin, onMenuToggle, userMenuOpen, onUserMenuToggle, onLogout }) {
  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between relative z-30"
    >
      <div className="flex items-center gap-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onMenuToggle}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <Menu className="w-6 h-6 text-gray-700" />
        </motion.button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HR Policy Review System</h1>
          <p className="text-sm text-gray-500">{isAdmin ? 'Administration Panel' : 'Employee Portal'}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="hidden md:flex flex-col items-end">
          <p className="font-semibold text-gray-900">{employee?.name}</p>
          <p className="text-sm text-gray-500">{employee?.emp_dept}</p>
        </div>
        <UserMenuDropdown 
          employee={employee}
          isOpen={userMenuOpen}
          onToggle={onUserMenuToggle}
          onLogout={onLogout}
        />
      </div>
    </motion.header>
  );
}

// ============ USER MENU DROPDOWN ============
function UserMenuDropdown({ employee, isOpen, onToggle, onLogout }) {
  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        onClick={onToggle}
        className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold hover:bg-indigo-700"
      >
        {employee?.name?.charAt(0) || 'U'}
      </motion.button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
          >
            <div className="p-4 border-b border-gray-200">
              <p className="font-semibold text-gray-900">{employee?.name}</p>
              <p className="text-sm text-gray-500">{employee?.email}</p>
              <p className="text-xs text-gray-400 mt-1">ID: {employee?.display_emp_id}</p>
            </div>
            <motion.button
              whileHover={{ bg: '#f3f4f6' }}
              onClick={onLogout}
              className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2 font-medium"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ DASHBOARD VIEW ============
function Dashboard({ employee, isAdmin, policies, policyViews, policyStats, encryptedId }) {
  const [hoveredTooltip, setHoveredTooltip] = useState(null);

  // Calculate status counts
  const acceptedCount = Object.values(policyViews).filter(v => v?.accepted).length;
  const reviewedOnlyCount = Object.values(policyViews).filter(v => v?.viewed && !v?.accepted).length;
  const notOpenedCount = policies.length - Object.keys(policyViews).length;

  // Admin Dashboard Calculations
  const totalEmployeesViews = Object.values(policyStats).reduce((acc, stat) => acc + (stat?.totalEmployees || 0), 0);
  const totalAccepted = Object.values(policyStats).reduce((acc, stat) => acc + (stat?.acceptedCount || 0), 0);
  const totalReviewed = Object.values(policyStats).reduce((acc, stat) => acc + (stat?.reviewedCount || 0), 0);
  const totalNotOpened = Object.values(policyStats).reduce((acc, stat) => acc + (stat?.notOpenedCount || 0), 0);
  const avgCompletion = policies.length > 0 
    ? (Object.values(policyStats).reduce((acc, stat) => acc + parseFloat(stat?.completion || 0), 0) / policies.length).toFixed(1)
    : 0;

  // Get unique employee count and overall engagement
  const uniqueEmployeeCount = Math.ceil(totalEmployeesViews / policies.length);
  const overallEmployeeCount = uniqueEmployeeCount * policies.length;

  // Get compliance status breakdown
  // const fullyCompliant = policies.filter(p => {
  //   const stats = policyStats[p.id] || {};
  //   return stats.totalEmployees && (stats.acceptedCount / stats.totalEmployees) === 1;
  // }).length;

  // const inProgress = policies.filter(p => {
  //   const stats = policyStats[p.id] || {};
  //   if (!stats.totalEmployees) return false;
  //   const pct = (stats.acceptedCount / stats.totalEmployees) * 100;
  //   return pct > 0 && pct < 100;
  // }).length;

  // const notStarted = policies.filter(p => {
  //   const stats = policyStats[p.id] || {};
  //   return !stats.totalEmployees || stats.acceptedCount === 0;
  // }).length;

  // const criticalPolicies = policies.filter(p => {
  //   const stats = policyStats[p.id] || {};
  //   if (!stats.totalEmployees) return true;
  //   const pct = (stats.acceptedCount / stats.totalEmployees) * 100;
  //   return pct < 50;
  // }).length;

  const fullyCompliant = policies.filter(p => {
  const stats = policyStats[String(p.id)] || {};
  return stats.totalEmployees > 0 && stats.acceptedCount === stats.totalEmployees;
}).length;

const inProgress = policies.filter(p => {
  const stats = policyStats[String(p.id)] || {};
  if (!stats.totalEmployees) return false;
  const pct = (stats.acceptedCount / stats.totalEmployees) * 100;
  return pct > 0 && pct < 100;
}).length;

const notStarted = policies.filter(p => {
  const stats = policyStats[String(p.id)] || {};
  return stats.totalEmployees > 0 && stats.acceptedCount === 0 && stats.reviewedCount === 0;
}).length;

const criticalPolicies = policies.filter(p => {
  const stats = policyStats[String(p.id)] || {};
  if (!stats.totalEmployees) return false;
  const pct = (stats.acceptedCount / stats.totalEmployees) * 100;
  return pct < 50;
}).length;

console.log('policyStats keys:', Object.keys(policyStats));
console.log('policy ids:', policies.map(p => p.id));
console.log('types:', typeof Object.keys(policyStats)[0], typeof policies[0]?.id);

  // Tooltip Component
  const TooltipBox = ({ id, title, description, children }) => (
    <div className="relative group">
      {children}
      <div 
        className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 bg-gray-900 text-white text-sm rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
        onMouseEnter={() => setHoveredTooltip(id)}
        onMouseLeave={() => setHoveredTooltip(null)}
      >
        <div className="font-semibold text-white mb-1">{title}</div>
        <div className="text-gray-200 text-xs leading-relaxed">{description}</div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900"></div>
      </div>
    </div>
  );

  // KPI Card Component with Tooltip
  const KPICard = ({ id, title, value, icon: Icon, color, subtitle, description }) => (
    <TooltipBox
      id={id}
      title={title}
      description={description}
    >
      <div className={`bg-white rounded-xl shadow-sm p-6 border-l-4 hover:shadow-lg transition-shadow cursor-help h-full flex flex-col`}
        style={{ borderLeftColor: color }}>
        <div className="flex items-start justify-between flex-1">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-sm text-gray-600 font-medium">{title}</p>
              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0" />
            </div>
            <p className={`text-3xl font-bold mb-2`} style={{ color }}>{value}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          <div className="p-3 rounded-lg ml-4 flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
        </div>
      </div>
    </TooltipBox>
  );

  // Section Card with Tooltip
  const SectionCard = ({ id, title, description, children }) => (
    <TooltipBox
      id={id}
      title={title}
      description={description}
    >
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors cursor-help" />
        </div>
        {children}
      </div>
    </TooltipBox>
  );

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 hover:shadow-lg transition-shadow" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2" style={{ color }}>{value}</p>
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">
            {isAdmin ? 'Dashboard' : `Welcome, ${employee?.name}! 👋`}
          </h1>
          <p className="text-gray-600 mt-2">
            {isAdmin 
              ? 'Comprehensive policy compliance and employee acknowledgment overview' 
              : `Department: ${employee?.emp_dept}`}
          </p>
        </div>

        {/* Admin - Top Level Metrics */}
        {isAdmin && (
          <>
            {/* Primary KPI Cards with Tooltips */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <KPICard
                id="total-policies"
                title="Total Policies"
                value={policies.length}
                icon={FileText}
                color="#3b82f6"
                description="Total number of policies currently available in the system. Each policy requires employee acknowledgment and acceptance."
              />

              <KPICard
                id="avg-completion"
                title="Avg Completion"
                value={`${avgCompletion}%`}
                icon={TrendingUp}
                color="#10b981"
                description={`Average completion rate across all policies. Calculated by summing up all policy completion percentages and dividing by ${policies.length} total policies. A higher percentage indicates better overall compliance.`}
              />

              <KPICard
                id="critical-policies"
                title="Critical Policies"
                value={criticalPolicies}
                icon={AlertCircle}
                color="#f97316"
                subtitle="Below 50% compliance"
                description="Policies with completion rate below 50%. These policies require immediate attention and action to improve employee compliance and acknowledgment rates."
              />

              <KPICard
                id="active-employees"
                title="Overall Engagement"
                value={overallEmployeeCount}
                icon={Users}
                color="#a855f7"
                subtitle={`${uniqueEmployeeCount} unique × ${policies.length} policies`}
                description={`Total employee-policy engagement count. Calculated as ${uniqueEmployeeCount} unique employees × ${policies.length} total policies = ${overallEmployeeCount} total engagements. Original active employee count: ${totalEmployeesViews}`}
              />
            </div>

            {/* Compliance Status Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Status Breakdown */}
              <SectionCard
                id="policy-status"
                title="Policy Status Distribution"
                description="Breakdown of all policies by their compliance status. Fully Compliant means 100% of employees accepted. In Progress means some employees have accepted. Not Started means no employees have accepted yet."
              >
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                        Fully Compliant
                      </span>
                      <span className="text-sm font-bold text-green-600">{fullyCompliant}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${(fullyCompliant / policies.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                        In Progress
                      </span>
                      <span className="text-sm font-bold text-blue-600">{inProgress}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${(inProgress / policies.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span>
                        Not Started
                      </span>
                      <span className="text-sm font-bold text-red-600">{notStarted}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-red-500 h-2 rounded-full"
                        style={{ width: `${(notStarted / policies.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* Employee Engagement */}
              <SectionCard
                id="employee-engagement"
                title="Employee Engagement"
                description="Summary of employee responses across all policies. Accepted = employees who reviewed and accepted the policy. Reviewed = employees who viewed but didn't accept. Not Opened = employees who haven't viewed the policy yet."
              >
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 ring-1 ring-green-200">
                    <div className="text-sm text-green-700 font-semibold">Accepted</div>
                    <div className="text-3xl font-bold text-green-600 mt-2">{totalAccepted}</div>
                    <div className="text-xs text-green-600 mt-1">Reviewed & Accepted</div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 ring-1 ring-amber-200">
                    <div className="text-sm text-amber-700 font-semibold">Reviewed</div>
                    <div className="text-3xl font-bold text-amber-600 mt-2">{totalReviewed}</div>
                    <div className="text-xs text-amber-600 mt-1">Viewed Only</div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 ring-1 ring-red-200">
                    <div className="text-sm text-red-700 font-semibold">Not Opened</div>
                    <div className="text-3xl font-bold text-red-600 mt-2">{totalNotOpened}</div>
                    <div className="text-xs text-red-600 mt-1">Pending Review</div>
                  </div>
                </div>
              </SectionCard>

              {/* Compliance Health */}
              <SectionCard
                id="compliance-health"
                title="Compliance Health"
                description="Overall system health assessment based on completion rates. Excellent (80%+): Strong compliance. Good (60-79%): Acceptable performance. Fair (40-59%): Needs improvement. Critical (<40%): Urgent action required."
              >
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700">Overall Status</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        avgCompletion >= 80 ? 'bg-green-100 text-green-700' :
                        avgCompletion >= 60 ? 'bg-blue-100 text-blue-700' :
                        avgCompletion >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {avgCompletion >= 80 ? 'Excellent' :
                         avgCompletion >= 60 ? 'Good' :
                         avgCompletion >= 40 ? 'Fair' :
                         'Critical'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          avgCompletion >= 80 ? 'bg-green-500' :
                          avgCompletion >= 60 ? 'bg-blue-500' :
                          avgCompletion >= 40 ? 'bg-amber-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${avgCompletion}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-600 space-y-2">
                      <div className="flex justify-between">
                        <span>Average Policy Completion:</span>
                        <span className="font-bold text-gray-900">{avgCompletion}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Policies Requiring Attention:</span>
                        <span className="font-bold text-orange-600">{criticalPolicies}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Target: 90%</span>
                        <span className={`font-bold ${parseFloat(avgCompletion) >= 90 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(avgCompletion) >= 90 ? '✓ On Track' : '✗ Below Target'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          </>
        )}

        {/* User Dashboard Stats */}
        {!isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard title="Total Policies" value={policies.length} icon={FileText} color="#3b82f6" />
            <StatCard title="Not Opened" value={notOpenedCount} icon={Clock} color="#ef4444" />
            <StatCard title="Reviewed Only" value={reviewedOnlyCount} icon={Eye} color="#f59e0b" />
            <StatCard 
              title="Reviewed & Accepted" 
              value={acceptedCount} 
              icon={CheckCircle} 
              color="#10b981" 
            />
          </div>
        )}

        {/* Policies Section */}
        <PolicyListSection
          policies={policies}
          policyViews={policyViews}
          policyStats={policyStats}
          isAdmin={isAdmin}
          encryptedId={encryptedId}
        />
      </div>
    </motion.div>
  );
}

// ============ POLICY LIST SECTION ============
function PolicyListSection({ policies, policyViews, policyStats, isAdmin, encryptedId }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">📋 All Policies</h2>
      <div className="space-y-3">
        {policies.map((policy) => {
          const stats = policyStats[String(policy.id)] || {};
          return (
            <PolicyListItem
              key={policy.id}
              policy={policy}
              stats={stats}
              policyViews={policyViews}
              isAdmin={isAdmin}
              encryptedId={encryptedId}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============ POLICY LIST ITEM ============
// ============ POLICY LIST ITEM ============
function PolicyListItem({ policy, stats, policyViews, isAdmin, encryptedId }) {
  const viewStatus = policyViews[policy.id];

  // Determine status
  let status = 'Not Opened';
  let statusColor = 'bg-red-100 text-red-800';
  let statusIcon = Clock;
  let statusText = '⏳ Not Opened';

  if (viewStatus?.viewed) {
    if (viewStatus.accepted) {
      status = 'Reviewed and Accepted';
      statusColor = 'bg-green-100 text-green-800';
      statusIcon = CheckCircle;
      statusText = '✓ Reviewed & Accepted';
    } else {
      status = 'Reviewed';
      statusColor = 'bg-yellow-100 text-yellow-800';
      statusIcon = Eye;
      statusText = '👁 Reviewed';
    }
  }

  const StatusIcon = statusIcon;

  return (
    <div className="flex items-center justify-between pb-3 border-b border-gray-200 last:border-0">
      <div className="flex items-center gap-4 flex-1">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            status === 'Reviewed and Accepted'
              ? 'bg-green-100'
              : status === 'Reviewed'
              ? 'bg-yellow-100'
              : 'bg-red-100'
          }`}
        >
          <StatusIcon
            className={`w-6 h-6 ${
              status === 'Reviewed and Accepted'
                ? 'text-green-600'
                : status === 'Reviewed'
                ? 'text-yellow-600'
                : 'text-red-600'
            }`}
          />
        </div>

        <div className="flex-1">
          <p className="font-medium text-gray-900">{policy.title}</p>
          <p className="font-medium text-gray-400">{policy.description}</p>
          {isAdmin && (
            // <p className="text-xs text-gray-500">
            //   Reviews: {stats.total_views || 0} | Users: {stats.unique_employees || 0}
            // </p>
            <p className="text-xs text-gray-500">
              Accepted: {stats.acceptedCount || 0} | Reviewed: {stats.reviewedCount || 0} | Not Opened: {stats.notOpenedCount || 0}
            </p>
          )}

          {!isAdmin && viewStatus?.viewed && (
            <p className="text-xs text-gray-500">
              Last viewed: {new Date(viewStatus.last_viewed).toLocaleDateString()} | Views:{' '}
              {viewStatus.view_count}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {!isAdmin && (
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColor}`}>
            {statusText}
          </span>
        )}

        {/* FIXED: Proper <a> tag */}
        <a
          href={`/policy/${encryptedId}/document/${policy.id}`}
          className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded font-semibold"
        >
          View
        </a>
      </div>
    </div>
  );
}


// ============ POLICIES VIEW ============
function PoliciesView({ employee, policies, encryptedId, policyViews, policyStats, isAdmin, onViewDetails }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-8"
    >
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{isAdmin ? 'All Policies' : 'View All Policies'}</h1>
        <p className="text-gray-600 mt-2">{isAdmin ? 'Manage and monitor all policies' : 'You can view any policy anytime'}</p>
      </div>

      <div className="grid gap-6">
        {policies.map((policy, index) => {
          const stats = policyStats[String(policy.id)] || {};
          return (
            <PolicyCard
              key={policy.id}
              policy={policy}
              stats={stats}
              policyViews={policyViews}
              isAdmin={isAdmin}
              encryptedId={encryptedId}
              onViewDetails={onViewDetails}
              index={index}
            />
          );
        })}
      </div>
    </motion.div>
  );
}

// ============ POLICY CARD ============
function PolicyCard({ policy, stats, policyViews, isAdmin, encryptedId, onViewDetails, index }) {
  const viewStatus = policyViews[policy.id];

  // Determine status
  let status = 'Not Opened';
  let statusColor = 'bg-red-50 text-red-700 border border-red-200';
  let statusIcon = Clock;
  let statusBgColor = 'bg-red-100';
  let dotColor = 'bg-red-500';

  if (viewStatus?.viewed) {
    if (viewStatus.accepted) {
      status = 'Reviewed and Accepted';
      statusColor = 'bg-green-50 text-green-700 border border-green-200';
      statusIcon = CheckCircle;
      statusBgColor = 'bg-green-100';
      dotColor = 'bg-green-500';
    } else {
      status = 'Reviewed';
      statusColor = 'bg-amber-50 text-amber-700 border border-amber-200';
      statusIcon = Eye;
      statusBgColor = 'bg-amber-100';
      dotColor = 'bg-amber-500';
    }
  }

  const StatusIcon = statusIcon;
  const completion = stats.totalEmployees ? ((stats.acceptedCount / stats.totalEmployees) * 100).toFixed(1) : 0;
  
  const getProgressColor = (value) => {
    if (value === 100) return 'from-green-400 to-green-600';
    if (value >= 75) return 'from-blue-400 to-blue-600';
    if (value >= 50) return 'from-amber-400 to-amber-600';
    return 'from-red-400 to-red-600';
  };

  const getProgressBarColor = (value) => {
    if (value === 100) return 'bg-green-500';
    if (value >= 75) return 'bg-blue-500';
    if (value >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="group relative bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
    >
      {/* Gradient Accent Bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getProgressColor(completion)}`}></div>

      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Left Section - Policy Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-4 mb-4">
              {/* Icon Badge */}
              <div className={`flex-shrink-0 p-3 rounded-xl ${statusBgColor} ring-1 ring-offset-2 ring-offset-white transition-transform group-hover:scale-110`}>
                <StatusIcon
                  className={`w-6 h-6 ${
                    status === 'Reviewed and Accepted'
                      ? 'text-green-600'
                      : status === 'Reviewed'
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                />
              </div>

              {/* Title and Description */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                  {policy.title}
                </h3>
                <p className="text-sm text-gray-600 mt-2 line-clamp-2 leading-relaxed">
                  {policy.description}
                </p>
              </div>
            </div>

            {/* Status Badge - Mobile */}
            <div className="lg:hidden flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${statusColor}`}>
                <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
                {status}
              </span>
            </div>
          </div>

          {/* Center Section - Admin Stats & Progress */}
          {isAdmin && (
            <div className="w-full lg:w-auto space-y-4">
              {/* Progress Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Completion</span>
                  </div>
                  <span className={`text-sm font-bold bg-gradient-to-r ${getProgressColor(completion)} bg-clip-text text-transparent`}>
                    {completion}%
                  </span>
                </div>
                <div className="w-full lg:w-56 h-2.5 bg-gray-200 rounded-full overflow-hidden shadow-sm">
                  <div
                    className={`h-full transition-all duration-500 ease-out ${getProgressBarColor(completion)} rounded-full shadow-md`}
                    style={{ width: `${completion}%` }}
                  ></div>
                </div>
              </div>

              {/* Stats Grid - Desktop */}
              <div className="hidden md:grid grid-cols-4 gap-3 pt-2">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 ring-1 ring-green-200">
                  <div className="text-xs text-green-700 font-semibold uppercase tracking-wide">Accepted</div>
                  <div className="text-xl font-bold text-green-600 mt-1">{stats.acceptedCount || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 ring-1 ring-amber-200">
                  <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Reviewed</div>
                  <div className="text-xl font-bold text-amber-600 mt-1">{stats.reviewedCount || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 ring-1 ring-red-200">
                  <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">Not Opened</div>
                  <div className="text-xl font-bold text-red-600 mt-1">{stats.notOpenedCount || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-3 ring-1 ring-gray-300">
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-gray-700" />
                    <span className="text-xs text-gray-700 font-semibold uppercase tracking-wide">Total</span>
                  </div>
                  <div className="text-xl font-bold text-gray-800 mt-1">{stats.totalEmployees || 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* Right Section - Status Badge & Actions */}
          <div className="flex flex-col gap-4 w-full lg:w-auto">
            {/* Status Badge - Desktop */}
            {!isAdmin && (
              <div className="hidden lg:block">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg ${statusColor}`}>
                  <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
                  {status}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 w-full lg:w-auto">
              <button
                onClick={() => onViewDetails(policy.id)}
                className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-sm hover:shadow-md text-sm group/btn"
              >
                <Eye className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                <span>Detailed View</span>
              </button>

              <a
                href={`/policy/${encryptedId}/document/${policy.id}`}
                className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 border border-gray-300 text-sm group/btn"
              >
                <Download className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                <span>Document</span>
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Admin Stats */}
        {isAdmin && (
          <div className="md:hidden pt-4 mt-4 border-t border-gray-200">
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-green-50 rounded-lg p-3 ring-1 ring-green-200 text-center">
                <div className="text-xs text-green-700 font-semibold">Accepted</div>
                <div className="text-lg font-bold text-green-600 mt-1">{stats.acceptedCount || 0}</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 ring-1 ring-amber-200 text-center">
                <div className="text-xs text-amber-700 font-semibold">Reviewed</div>
                <div className="text-lg font-bold text-amber-600 mt-1">{stats.reviewedCount || 0}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 ring-1 ring-red-200 text-center">
                <div className="text-xs text-red-700 font-semibold">Not Opened</div>
                <div className="text-lg font-bold text-red-600 mt-1">{stats.notOpenedCount || 0}</div>
              </div>
              <div className="bg-gray-100 rounded-lg p-3 ring-1 ring-gray-300 text-center">
                <div className="text-xs text-gray-700 font-semibold">Total</div>
                <div className="text-lg font-bold text-gray-800 mt-1">{stats.totalEmployees || 0}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}


// ============ POLICY DETAILS PAGE (ADMIN ONLY) ============
function PolicyDetailsPage({ policyId, policyName, onBack }) {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/policy/${policyId}/views-detail`);
        if (!res.ok) throw new Error('Failed to fetch details');
        
        const data = await res.json();
        console.log('Fetched policy details:', data);
        setDetails(data.views || []);
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load policy details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [policyId]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-8"
    >
      <div className="mb-6">
        <motion.button
          whileHover={{ x: -5 }}
          onClick={onBack}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Policies
        </motion.button>
        <h1 className="text-3xl font-bold text-gray-900">📊 Review Details</h1>
        <p className="text-gray-600 mt-2">{policyName}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-96">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-12 h-12 border-4 border-indigo-300 border-t-indigo-600 rounded-full"
          />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      ) : details.length === 0 ? (
        <EmptyDetailsState />
      ) : (
        <UserDetailsTable details={details} policyId={policyId} />
      )}
    </motion.div>
  );
}

// ============ EMPTY DETAILS STATE ============
function EmptyDetailsState() {
  return (
    <div className="bg-white rounded-lg shadow-md p-12 text-center">
      <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <p className="text-gray-600 text-lg">No users have reviewed this policy yet</p>
      <p className="text-gray-500 text-sm mt-2">Details will appear once users review the policy</p>
    </div>
  );
}

// ============ USER DETAILS TABLE ============
function UserDetailsTable({ details, policyId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md overflow-hidden"
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <TableHeader />
          <tbody>
            {details.map((view, idx) => (
              <TableRow key={idx} view={{ ...view, policy_id: policyId }} />
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

// ============ TABLE HEADER ============
function TableHeader() {
  return (
    <thead className="bg-gray-50 border-b border-gray-200">
      <tr>
        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Employee Name</th>
        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Total Reviews</th>
        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">First Viewed</th>
        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Last Reviewed</th>
      </tr>
    </thead>
  );
}

// ============ TABLE ROW ============
function TableRow({ view }) {
  const [showViewHistory, setShowViewHistory] = useState(false);
  const [viewHistory, setViewHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const handleViewCount = async () => {
    if (showViewHistory) {
      setShowViewHistory(false);
      return;
    }

    try {
      setLoadingHistory(true);

      console.log("policyId from row:", view.policy_id, view.policyId);
      console.log("employeeId from row:", view.employee_id, view.employeeId);
      
      const res = await fetch(`${API_URL}/api/policy/${view.policy_id}/employee/${view.display_emp_id}/views`);
      
      if (res.ok) {
        const data = await res.json();
        setViewHistory(data.views || []);
        setShowViewHistory(true); 
      } else {
        console.error('API Error: Failed to fetch view details.');
      }

    } catch (err) {
      console.error('Network Error fetching view history:', err);
      setShowViewHistory(false); 
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <>
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
        onClick={handleViewCount}
        layout
      >
        <td className="px-6 py-4 text-sm font-medium text-gray-900">{view.name}</td>
        <td className="px-6 py-4 text-sm text-gray-600">{view.email}</td>
        <td className="px-6 py-4 text-sm">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full font-bold hover:bg-indigo-200 transition"
          >
            {view.view_count}
          </motion.button>
        </td>
        <td className="px-6 py-4 text-sm text-gray-600">{new Date(view.first_viewed).toLocaleDateString()}</td>
        <td className="px-6 py-4 text-sm text-gray-600">{new Date(view.last_viewed).toLocaleDateString()}</td>
      </motion.tr>

      <AnimatePresence> 
        {showViewHistory && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-indigo-50 border-b-2 border-indigo-200"
          >
            <td colSpan="5" className="px-6 py-4">
              <ViewHistoryExpanded
                empName={view.name}
                viewHistory={viewHistory}
                loading={loadingHistory}
                onClose={() => setShowViewHistory(false)}
              />
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

// ============ VIEW HISTORY EXPANDED ============
function ViewHistoryExpanded({ empName, viewHistory, loading, onClose }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-900">View History - {empName}</h4>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 font-bold"
        >
          <X className="w-5 h-5" />
        </motion.button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full"
          />
        </div>
      ) : viewHistory.length === 0 ? (
        <p className="text-gray-600 text-sm py-2 bg-white p-4 rounded-lg shadow-inner border border-gray-100">
            <AlertCircle className="w-4 h-4 inline mr-2 text-yellow-500"/> 
            No view history available for this user and policy.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {viewHistory.map((view, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-white p-3 rounded-lg border border-indigo-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-600">View #{idx + 1}</span>
              </div>
              <p className="text-xs text-gray-600">
                {new Date(view.viewed_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
              <p className="text-xs text-gray-500">
                {new Date(view.viewed_at).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              {view.ip_address && view.ip_address !== 'unknown' && (
                <p className="text-xs text-gray-400 mt-1 truncate">IP: {view.ip_address}</p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ ADMIN POLICY PANEL ============
function AdminPolicyPanel({ isAdmin, employee }) {
  const [modalError, setModalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
const [filterYear, setFilterYear] = useState('');
const [filterMonth, setFilterMonth] = useState('');
const [filterPolicyId, setFilterPolicyId] = useState('');
const [filterFormat, setFilterFormat] = useState('excel');
const [filterPolicyName, setFilterPolicyName] = useState('');

const [selectedPolicy, setSelectedPolicy] = useState(null);
const [policyDetails, setPolicyDetails] = useState(null);
const [showDetails, setShowDetails] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    release_date: '',
    status: 'published',
    file: null
  });

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/policies?`;
      const params = [];
      if (filterYear) params.push(`year=${filterYear}`);
      if (filterMonth) params.push(`month=${filterMonth}`);

      const response = await fetch(url + params.join('&'));
      const data = await response.json();
      setPolicies(data.policies || []);
    } catch (error) {
      setMessage('Error loading policies');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, [filterYear, filterMonth]);

  const fetchPolicyDetails = async (policyId) => {
  try {
    setLoading(true);
    const response = await fetch(`${API_URL}/api/policy/${policyId}/views-detail`);
    const data = await response.json();
    setPolicyDetails(data.views || []);
    setShowDetails(true);
  } catch (error) {
    setMessage('Error loading policy details');
    console.error(error);
  } finally {
    setLoading(false);
  }
};

const handleSelectPolicy = async (policy) => {
  setSelectedPolicy(policy);
  await fetchPolicyDetails(policy.id);
};

const exportPolicyDetails = () => {
  if (!selectedPolicy || !policyDetails) {
    setMessage('Select a policy first');
    return;
  }

  try {
    const csvContent = [
      ['Policy Details Report'],
      ['Policy ID', selectedPolicy.id],
      ['Policy Name', selectedPolicy.title],
      ['Release Date', new Date(selectedPolicy.release_date).toLocaleDateString()],
      ['Status', selectedPolicy.status],
      [''],
      ['Employee ID', 'Employee Name', 'Email', 'Times Reviewed', 'First Viewed', 'Last Reviewed'],
      ...policyDetails.map(view => [
        view.display_emp_id,
        view.name,
        view.email,
        view.view_count,
        new Date(view.first_viewed).toLocaleDateString(),
        new Date(view.last_viewed).toLocaleDateString()
      ])
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `policy_${selectedPolicy.id}_${selectedPolicy.title}_details_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    setMessage('Report exported successfully');
    setTimeout(() => setMessage(''), 3000);
  } catch (error) {
    setMessage('Error exporting report');
    console.error(error);
  }
};


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({...prev, file: e.target.files[0]}));
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!formData.title || !formData.description || !formData.release_date) {
  //     setMessage('Fill all required fields');
  //     return;
  //   }

  //   if (!editingId && !formData.file) {
  //     setMessage('Upload a file for new policies');
  //     return;
  //   }

  //   try {
  //     const formDataToSend = new FormData();
  //     formDataToSend.append('title', formData.title);
  //     formDataToSend.append('description', formData.description);
  //     formDataToSend.append('release_date', formData.release_date);
  //     formDataToSend.append('status', formData.status);
  //     if (formData.file) formDataToSend.append('file', formData.file);

  //     const method = editingId ? 'PUT' : 'POST';
  //     const url = editingId ? `${API_URL}/api/policies/${editingId}` : `${API_URL}/api/policies`;

  //     const response = await fetch(url, {
  //       method: method,
  //       body: formDataToSend
  //     });

  //     const data = await response.json();
  //     if (data.success) {
  //       setMessage(editingId ? 'Updated successfully' : 'Created successfully');
  //       setShowModal(false);
  //       resetForm();
  //       fetchPolicies();
  //       setTimeout(() => setMessage(''), 3000);
  //     } else {
  //       setMessage(data.error || 'Error saving policy');
  //     }
  //   } catch (error) {
  //     setMessage('Error saving policy');
  //     console.error(error);
  //   }
  // };

 const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.title || !formData.description || !formData.release_date) {
      setModalError('Please fill all required fields.');
      return;
    }
    if (!editingId && !formData.file) {
      setModalError('Please upload a file for new policies.');
      return;
    }

    try {
      setIsSubmitting(true);
      setModalError('');

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('release_date', formData.release_date);
      formDataToSend.append('status', formData.status);
      if (formData.file) formDataToSend.append('file', formData.file);

      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_URL}/api/policies/${editingId}`
        : `${API_URL}/api/policies`;

      const response = await fetch(url, { method, body: formDataToSend });
      const data = await response.json();

      if (data.success) {
        setMessage(editingId ? '✅ Updated successfully' : '✅ Created successfully');
        setModalError('');
        setShowModal(false);
        resetForm();
        fetchPolicies();
        setTimeout(() => setMessage(''), 3000);
      } else {
        // Show inside modal
        setModalError(data.error || 'Error saving policy');
      }
    } catch (error) {
      setModalError('Network error. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }; 
  
  const handleEdit = (policy) => {
  // Fix date conversion issue
  let releaseDateValue = '';
  if (policy.release_date) {
    const date = new Date(policy.release_date);
    // Format as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    releaseDateValue = `${year}-${month}-${day}`;
  }

  setFormData({
    title: policy.title,
    description: policy.description,
    release_date: releaseDateValue,
    status: policy.status,
    file: null
  });
  setEditingId(policy.id);
  setShowModal(true);
};

const handleDelete = async (id) => {
  if (window.confirm('Delete this policy?')) {
    try {
      const response = await fetch(`${API_URL}/api/policies/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setMessage('Deleted successfully');
        if (selectedPolicy?.id === id) {
          setSelectedPolicy(null);
          setPolicyDetails(null);
          setShowDetails(false);
        }
        fetchPolicies();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error deleting policy');
      }
    } catch (error) {
      setMessage('Error deleting policy');
      console.error(error);
    }
  }
};

const handleGenerateReport = async () => {
  try {
    // Must select a policy
    if (!filterPolicyId) {
      setMessage('❌ Please select a policy to generate report');
      return;
    }

    // Get selected policy details
    const selectedPol = policies.find(p => p.id === parseInt(filterPolicyId));
    if (!selectedPol) {
      setMessage('❌ Policy not found');
      return;
    }

    let url = `${API_URL}/api/reports/excel?`;
    const params = [];

    // Always add policy ID
    params.push(`policyId=${filterPolicyId}`);
    params.push(`policyName=${encodeURIComponent(selectedPol.title)}`);

    // Add optional filters
    if (filterYear) {
      params.push(`year=${filterYear}`);
    }
    if (filterMonth) {
      params.push(`month=${filterMonth}`);
    }

    // Add format (for future PDF support)
    params.push(`format=${filterFormat}`);

    console.log('Generating report with filters:', params);
    
    window.location.href = url + params.join('&');
    
    setMessage(`✅ Report generated: ${selectedPol.title}${filterYear ? ` - ${filterYear}` : ''}${filterMonth ? ` - Month ${filterMonth}` : ''}`);
    setTimeout(() => setMessage(''), 3000);
  } catch (error) {
    setMessage('❌ Error generating report');
    console.error(error);
  }
};

// Step 3: ADD this handler for format change
const handleFormatChange = (e) => {
  const format = e.target.value;
  setFilterFormat(format);
  setMessage(`✅ Format changed to ${format.toUpperCase()}`);
  setTimeout(() => setMessage(''), 2000);
};

// Step 4: ADD this handler for policy change
const handlePolicyChange = (e) => {
  const policyId = e.target.value;
  const selectedPol = policies.find(p => p.id === parseInt(policyId));
  
  setFilterPolicyId(policyId);
  
  if (policyId) {
    setMessage(`✅ Selected: ${selectedPol.title}`);
    setTimeout(() => setMessage(''), 2000);
  } else {
    setMessage('ℹ️ Please select a policy');
  }
};



  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      release_date: '',
      status: 'published',
      file: null
    });
    setEditingId(null);
    setModalError(''); 
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-8 space-y-6"
    >
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📋 Policy Management</h1>
          <p className="text-gray-600 mt-1">Create, update, and manage company policies</p>
        </div>
        {isAdmin && (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => setShowModal(true)}
    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md"
  >
    <Plus className="w-5 h-5" />
    New Policy
  </motion.button>
)}
      </div>

      {/* <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-4 rounded-lg ${message.includes('Error') ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'}`}
          >
            {message}
          </motion.div>
        )}
      </AnimatePresence> */}

{/* Report Filters Section - ENHANCED */}
<div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-lg p-8 border-2 border-indigo-100">
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
        <Download className="w-6 h-6 text-white" />
      </div>
      <div>
        <h3 className="text-2xl font-bold text-gray-900">📊 Generate Policy Review Report</h3>
        <p className="text-sm text-gray-600 mt-1">Comprehensive employee review status with acceptance tracking</p>
      </div>
    </div>
    
    <div className="bg-blue-100 border-l-4 border-blue-500 p-4 rounded-lg mt-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">Report includes:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><span className="font-semibold text-green-700">Reviewed and Accepted</span> - Employees who read and accepted the policy</li>
            <li><span className="font-semibold text-yellow-700">Reviewed</span> - Employees who viewed but haven't accepted yet</li>
            <li><span className="font-semibold text-red-700">Not Opened</span> - Employees who haven't viewed the policy</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
    
    {/* Policy Selection - REQUIRED */}
    <div className="md:col-span-2">
      <label className="text-sm font-bold text-gray-800 block mb-3 flex items-center gap-2">
        <FileText className="w-4 h-4 text-indigo-600" />
        Select Policy <span className="text-red-600">*</span>
      </label>
      <select
        value={filterPolicyId}
        onChange={handlePolicyChange}
        className="w-full px-4 py-3 border-2 border-indigo-200 rounded-xl focus:ring-4 focus:ring-indigo-200 focus:border-indigo-500 font-medium bg-white hover:border-indigo-400 transition-all shadow-sm"
      >
        <option value="">-- Choose a Policy to Generate Report --</option>
        {policies.map((policy) => (
          <option key={policy.id} value={policy.id}>
            {policy.title} ({new Date(policy.release_date).toLocaleDateString()})
          </option>
        ))}
      </select>
      {filterPolicyId ? (
        <p className="text-xs text-green-700 mt-2 flex items-center gap-1 font-semibold">
          <CheckCircle className="w-3 h-3" /> Policy selected
        </p>
      ) : (
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Required - Select a policy to continue
        </p>
      )}
    </div>

    {/* Format Selection */}
    <div>
      <label className="text-sm font-bold text-gray-800 block mb-3 flex items-center gap-2">
        <Download className="w-4 h-4 text-green-600" />
        Export Format
      </label>
      <div className="relative">
        <select
          value={filterFormat}
          onChange={handleFormatChange}
          className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-4 focus:ring-green-200 focus:border-green-500 bg-green-50 font-semibold text-green-900 appearance-none cursor-pointer shadow-sm"
        >
          <option value="excel">📊 Excel Report</option>
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown className="w-5 h-5 text-green-700" />
        </div>
      </div>
      <p className="text-xs text-green-700 mt-2 flex items-center gap-1 font-semibold">
        <Check className="w-3 h-3" /> Excel format (.xlsx)
      </p>
    </div>

    {/* Generate Button */}
    <div className="flex items-end">
      <motion.button
        onClick={handleGenerateReport}
        disabled={!filterPolicyId}
        whileHover={filterPolicyId ? { scale: 1.02 } : {}}
        whileTap={filterPolicyId ? { scale: 0.98 } : {}}
        className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold transition-all shadow-lg ${
          filterPolicyId
            ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white cursor-pointer'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        <Download className="w-5 h-5" />
        Generate Report
      </motion.button>
    </div>
  </div>

  {/* Filter Summary */}
  {filterPolicyId && (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 p-5 bg-white border-2 border-indigo-200 rounded-xl shadow-md"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-gray-900 mb-2">Report Configuration</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-600">Policy:</span>
              <p className="font-semibold text-gray-900 mt-1">{policies.find(p => p.id === parseInt(filterPolicyId))?.title}</p>
            </div>
            <div>
              <span className="text-gray-600">Date Range:</span>
              <p className="font-semibold text-gray-900 mt-1">All Time</p>
            </div>
            <div>
              <span className="text-gray-600">Format:</span>
              <p className="font-semibold text-gray-900 mt-1">Excel (.xlsx)</p>
            </div>
            <div>
              <span className="text-gray-600">Includes:</span>
              <p className="font-semibold text-gray-900 mt-1">All Active Employees</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )}
</div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Title</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Release Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                {isAdmin  && (
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">File Type</th>
                )}
                {isAdmin  && (
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <p className="text-gray-600">Loading...</p>
                  </td>
                </tr>
              ) : policies.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <p className="text-gray-600">No policies found</p>
                  </td>
                </tr>
              ) : (
                policies.map((policy, index) => (
                  <motion.tr
                    key={policy.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-gray-200 hover:bg-gray-50 transition"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{policy.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(policy.release_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        policy.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {policy.status === 'published' ? '📢 Published' : '📝 Draft'}
                      </span>
                    </td>
                    {isAdmin && (
                    <td className="px-6 py-4 text-sm text-gray-600">{policy.file_type}</td>
                    )}
                    {isAdmin && (
                    <td className="px-6 py-4 text-sm space-x-2 flex">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleEdit(policy)}
                        className="text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        <Edit2 className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleDelete(policy.id)}
                        className="text-red-600 hover:text-red-800 font-semibold"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </td>
                    )}
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingId ? '✏️ Edit Policy' : '➕ New Policy'}
                </h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  onClick={() => {setShowModal(false); resetForm();}}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </motion.button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Policy Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter policy title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter policy description"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Release Date *
                    </label>
                    <input
                      type="date"
                      name="release_date"
                      value={formData.release_date}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div>
  <label className="text-sm font-medium text-gray-700 block mb-2">
    Status
  </label>
  <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-green-50 flex items-center">
    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
      📢 Published
    </span>
  </div>
  <p className="text-xs text-gray-500 mt-1">Status is fixed as Published</p>
</div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Policy File {!editingId && '*'}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-500 transition cursor-pointer">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf"
                      id="file-input"
                      style={{display: 'none'}}
                    />
                    <label htmlFor="file-input" style={{cursor: 'pointer'}}>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-700 font-medium">
                        {formData.file ? formData.file.name : 'Click to upload or drag & drop'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">PDF or Word documents up to 50MB</p>
                    </label>
                  </div>
                </div>

                {/* <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <motion.button
                    whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`flex-1 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                      isSubmitting
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                        {editingId ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingId ? 'Update Policy' : 'Create Policy'
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {setShowModal(false); resetForm();}}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold"
                  >
                    Cancel
                  </motion.button>
                </div> */}

                <div className="pt-4 border-t border-gray-200">

                  {/* Error inside modal */}
                  <AnimatePresence>
                    {modalError && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2"
                      >
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700 font-medium">{modalError}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex gap-4">
                    <motion.button
                      whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`flex-1 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                        isSubmitting
                          ? 'bg-indigo-400 cursor-not-allowed'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                          </svg>
                          {editingId ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        editingId ? 'Update Policy' : 'Create Policy'
                      )}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => { setShowModal(false); resetForm(); }}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold"
                    >
                      Cancel
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============ STAT CARD ============
function StatCard({ title, value, icon: Icon, color }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-600"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${color} w-12 h-12 rounded-lg flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
}